import { useCallback, useRef, useState } from "react";
import { deepseekClient } from "@/core/services/deepseek/DeepSeekClient";
import {
  makeChatMessage,
  useChatbotStore,
} from "@/core/stores/chatbot.store";
import { ChatMessage, ToolCallRecord } from "@/core/models/chat.model";

export function useChatStream(conversationId: string | null) {
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const store = useChatbotStore();

  const sendMessage = useCallback(
    async (text: string) => {
      if (!conversationId || !text.trim() || loading) return;

      const conv = useChatbotStore
        .getState()
        .conversations.find((c) => c.id === conversationId);
      if (!conv) return;

      const userMsg = makeChatMessage("user", text.trim());
      store.addMessage(conversationId, userMsg);

      const assistantMsg: ChatMessage = makeChatMessage("assistant", "", {
        isStreaming: true,
        toolCalls: [],
      });
      store.addMessage(conversationId, assistantMsg);

      setLoading(true);
      const ac = new AbortController();
      abortRef.current = ac;

      const history = conv.messages
        .filter((m) => m.role === "user" || m.role === "assistant")
        .filter((m) => m.content)
        .map((m) => ({
          role: m.role as "user" | "assistant",
          content: m.content,
        }));

      let buffered = "";
      const toolCalls: ToolCallRecord[] = [];

      await deepseekClient.chat(history, text.trim(), {
        signal: ac.signal,
        onDelta: (delta) => {
          buffered += delta;
          store.updateMessage(conversationId, assistantMsg.id, {
            content: buffered,
          });
        },
        onClearDisplay: () => {
          buffered = "";
          store.updateMessage(conversationId, assistantMsg.id, {
            content: "",
          });
        },
        onToolCallStart: (call) => {
          toolCalls.push({
            id: call.id,
            name: call.name,
            arguments: call.arguments,
            status: "running",
          });
          store.updateMessage(conversationId, assistantMsg.id, {
            toolCalls: [...toolCalls],
          });
        },
        onToolCallEnd: (call) => {
          const idx = toolCalls.findIndex((c) => c.id === call.id);
          if (idx >= 0) {
            toolCalls[idx] = {
              id: call.id,
              name: call.name,
              arguments: call.arguments,
              status: call.status,
              result: call.result,
            };
          }
          store.updateMessage(conversationId, assistantMsg.id, {
            toolCalls: [...toolCalls],
          });
        },
        onDone: (finalText) => {
          store.updateMessage(conversationId, assistantMsg.id, {
            content: finalText || buffered,
            isStreaming: false,
          });
          setLoading(false);
        },
        onError: (err) => {
          store.updateMessage(conversationId, assistantMsg.id, {
            content:
              (buffered ? buffered + "\n\n" : "") +
              `**Lỗi:** ${err.message}`,
            isStreaming: false,
          });
          setLoading(false);
        },
      });
    },
    [conversationId, loading, store]
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    setLoading(false);
  }, []);

  return { sendMessage, stop, loading };
}
